import React, { useState, useEffect, useContext } from 'react';
import './BuildOffer.scss';
import { useHistory } from 'react-router-dom';
import TitleHeader from '../../components/molecules/TitleHeader/TitleHeader';
import Loading from '../../components/atoms/Loading/Loading';
import ErrorBanner from '../../components/molecules/ErrorBanner/ErrorBanner';
import Quote from '../../components/molecules/Quote/Quote';
import { unitUnavailableBanner, generic } from '../../content/errors.json';
import { UNIT_STATUSES } from '../../appConstants';
import { PATHS } from '../../navigationConstants';
import { UserContext } from '../../providers/UserContext';
import { getQuotesWithUnits } from '../../api/salesforce';
import { BasketContext } from '../../providers/BasketContext';
import { WalesContext } from '../../providers/WalesContext';
import { buildOfferCopy } from '../../content/main.json';

const BuildOffer = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [noUnitsAvailable, setNoUnitsAvailable] = useState(false);
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(null);
  const [selectedQuoteIndex, setSelectedQuoteIndex] = useState(null);
  const [chosenOfferId, setChosenOfferId] = useState(null);
  const [periodicity, setPeriodicity] = useState(null);
  const history = useHistory();
  const { opportunityId, firstName } = useContext(UserContext);
  const { setBasketData } = useContext(BasketContext);
  const { setIsWales } = useContext(WalesContext);
  const {
    title,
    category,
    info,
    welcome,

  } = buildOfferCopy;

  const onBuildOffer = (unitIndex, offerId, periodicityValue) => {
    const unit = units[unitIndex];

    if (offerId && unit) {
      setBasketData({
        offerId,
        unitName: unit.unitName,
        postCode: unit.postCode,
        unitId: unit.unitId,
        bedrooms: unit.bedrooms,
        furnished: unit.furnished,
        monthlyRent: unit.monthlyRent,
        wishList: unit.wishList,
        moveInDate: unit.moveInStart || null,
        periodicity: periodicityValue,
      });
      return true;
    }

    return false;
  };

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const data = await getQuotesWithUnits(opportunityId);
        if (cancelled) {
          return;
        }

        setUnits(data);

        const unavailableUnits = data.filter(
          (unit) => !UNIT_STATUSES.includes(unit.unitStatus),
        );
        const isWales = data.some((unit) => unit.isWales === true);
        setIsWales(isWales);
        setNoUnitsAvailable(unavailableUnits.length === data.length);
        setApiError(false);
      } catch (error) {
        console.error('[BuildOffer] Unable to load quote data.', error);
        if (!cancelled) {
          setApiError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (selectedUnitIndex === null || !units[selectedUnitIndex]) {
      return;
    }

    const firstQuote = units[selectedUnitIndex].quotes && units[selectedUnitIndex].quotes[0];
    if (!firstQuote) {
      return;
    }

    setSelectedQuoteIndex(0);
    setChosenOfferId(firstQuote.id);
    setPeriodicity((firstQuote.periodicity || '').split(';')[0] || null);
  }, [selectedUnitIndex, units]);

  const onReserveHome = (index) => {
    const unit = units[index];

    if (!unit) {
      return;
    }

    const firstQuote = unit.quotes && unit.quotes[0];
    const reserveOfferId = selectedUnitIndex === index
      ? chosenOfferId
      : firstQuote && firstQuote.id;
    const reservePeriodicity = selectedUnitIndex === index
      ? periodicity
      : (firstQuote && (firstQuote.periodicity || '').split(';')[0]) || null;

    if (!reservePeriodicity || !reserveOfferId) {
      return;
    }

    setSelectedUnitIndex(index);
    setSelectedQuoteIndex(0);
    setChosenOfferId(reserveOfferId);
    setPeriodicity(reservePeriodicity);

    const saved = onBuildOffer(index, reserveOfferId, reservePeriodicity);
    if (saved) {
      history.push(PATHS.HOUSEHOLD);
    }
  };

  return (
    <div className="build-offer">
      <TitleHeader
        title={title}
        large
      />
      <h3>
        {welcome}
        {' '}

        {firstName}
      </h3>
      <div>
        {info}
      </div>
      <br />

      {loading && <Loading />}
      {noUnitsAvailable && (
        <ErrorBanner
          title={unitUnavailableBanner.title}
          body={unitUnavailableBanner.body}
        />
      )}
      {apiError && (
        <ErrorBanner
          title={generic.title}
          body={[generic.content]}
        />
      )}

      {/* STEP 1 - Choose Unit  */}
      <div className="row" data-testid="offerTiles">
        {units.map((unit, index) => (
          <div className="col-lg-4 build-offer__tile" key={unit.unitId}>
            <Quote
              selected={index === selectedUnitIndex}
              index={index}
              onReserveHome={onReserveHome}
              unit={unit}
            />
          </div>
        ))}
      </div>

    </div>
  );
};
export default BuildOffer;
